import { AgendaSlotType } from '@omecare/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  GetWorkScheduleSlotItemResponse,
  getWorkScheduleSlotItemSchema,
} from '@/hooks/queries/use-get-office-work-schedules-slots';
import http from '@/utils/http.service';

export type UpdateWorkScheduleSlotAssistantModeResponse =
  GetWorkScheduleSlotItemResponse;

export const useUpdateWorkScheduleSlotAssistantModeMutation = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({
      workScheduleSlotId,
      forceWithoutAssistant,
      type,
    }: {
      workScheduleSlotId: string;
      forceWithoutAssistant?: boolean;
      type?: AgendaSlotType;
    }): Promise<UpdateWorkScheduleSlotAssistantModeResponse> => {
      return http.workScheduleSlots
        .updateAssistantMode(workScheduleSlotId, {
          forceWithoutAssistant,
          type,
        })
        .then(({ response, transform }) => {
          return transform.handle({
            response,
            attributes: {
              startAt: true,
              endAt: true,
              rrule: true,
              exdate: true,
              sourceId: true,
              occurenceIndex: true,
              slotType: true,
              withAssistante: true,
              forceWithoutAssistant: true,
              dayOfWeek: true,
              periodOfTheDay: true,
              createdAt: true,
              updatedAt: true,
              medicalAssistantWorkDuration: true,
              medicalAssistantWorkPeriods: true,
              reconciliationWarningType: true,
              tag: true,
            },
            relations: {
              doctorAttending: true,
              sequence: true,
            },
            schema: getWorkScheduleSlotItemSchema,
          }) as UpdateWorkScheduleSlotAssistantModeResponse;
        });
    },
    onSuccess: () => {
      // Invalidate office work schedule slots query
      queryClient.invalidateQueries({
        queryKey: ['office-work-schedule-slots'],
      });
      toast.success(t('http.update-work-schedule-slot-assistant-mode.success'));
    },
    onError: () => {
      toast.error(t('http.update-work-schedule-slot-assistant-mode.error'));
    },
  });
};
